import axiosInstance from '../../../axiosInstance';
import apiEndpoints from '../../../api';
import { notify } from '../../../../utils/notification';

const clean = (obj) => {
    const out = {};
    Object.entries(obj || {}).forEach(([k, v]) => {
        if (v !== undefined && v !== null && v !== '') out[k] = v;
    });
    return out;
};

// Notices and circulars are the same record distinguished by `notice_type`.
// Every list-style request therefore carries an optional noticeType filter and
// the list's type tabs preset it (Notices -> NOTICE, Circulars -> CIRCULAR).
const listParams = (data) => clean({
    society_id: data?.societyId,
    page: data?.page || 1,
    limit: data?.limit || 10,
    search: data?.search,
    notice_type: data?.noticeType,
    status: data?.status,
    priority: data?.priority,
    category: data?.category,
    audience: data?.audience,
    wing_id: data?.wingId,
    from_date: data?.fromDate,
    to_date: data?.toDate,
    include_expired: data?.includeExpired ? 'true' : undefined,
});

export const getNoticesListReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_NOTICES_LIST_API, { params: listParams(action.data) })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load notices');
            return err;
        });
};

// Published-only feed for the signed-in user. The backend resolves the audience
// (ALL / OWNERS / TENANTS / a specific wing) from the token, so the FE never has
// to filter client-side.
export const getNoticeBoardReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_NOTICE_BOARD_API, { params: listParams(action.data) })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load notice board');
            return err;
        });
};

export const getNoticeDetailsReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_NOTICE_DETAILS_API.replace(':id', action.data.id);
    return axiosInstance.get(url, { params: clean({ society_id: action.data?.societyId }) })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load notice');
            return err;
        });
};

export const createNoticeReq = (action) => {
    const { onSuccess, onError, ...rest } = action.data || {};
    // Strip empty optionals so the BE defaults (audience ALL, status DRAFT, no
    // expiry) apply instead of the server receiving literal nulls.
    return axiosInstance.post(apiEndpoints.SOCIETY_CREATE_NOTICE_API, clean(rest))
        .then(res => {
            if (res?.success) notify('success', res.message || 'Notice created');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to create notice');
            return err;
        });
};

export const updateNoticeReq = (action) => {
    const url = apiEndpoints.SOCIETY_UPDATE_NOTICE_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.put(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Notice updated');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to update notice');
            return err;
        });
};

export const deleteNoticeReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_NOTICE_API.replace(':id', action.data.id);
    return axiosInstance.delete(url)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Notice deleted');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to delete notice');
            return err;
        });
};

export const publishNoticeReq = (action) => {
    const url = apiEndpoints.SOCIETY_PUBLISH_NOTICE_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.patch(url, clean(payload))
        .then(res => {
            if (res?.success) notify('success', res.message || 'Notice published');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to publish notice');
            return err;
        });
};

export const archiveNoticeReq = (action) => {
    const url = apiEndpoints.SOCIETY_ARCHIVE_NOTICE_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.patch(url, clean(payload))
        .then(res => {
            if (res?.success) notify('success', res.message || 'Notice archived');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to archive notice');
            return err;
        });
};

// Re-notify an already-published notice. Distinct from publish: the backend
// leaves status/target_count alone and only re-runs delivery.
export const resendNoticeReq = (action) => {
    const url = apiEndpoints.SOCIETY_RESEND_NOTICE_API.replace(':id', action.data.id);
    return axiosInstance.post(url, {})
        .then(res => {
            if (res?.success) notify('success', res.message || 'Notice resent');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to resend notice');
            return err;
        });
};

export const getNoticeCommentsReq = (action) => {
    const url = apiEndpoints.SOCIETY_NOTICE_COMMENTS_API.replace(':id', action.data.id);
    return axiosInstance.get(url)
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load comments');
            return err;
        });
};

export const addNoticeCommentReq = (action) => {
    const url = apiEndpoints.SOCIETY_NOTICE_COMMENTS_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.post(url, payload)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Comment added');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to add comment');
            return err;
        });
};

export const deleteNoticeCommentReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_NOTICE_COMMENT_API
        .replace(':id', action.data.id)
        .replace(':commentId', action.data.commentId);
    return axiosInstance.delete(url)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Comment deleted');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to delete comment');
            return err;
        });
};

// Attachments ride in as repeated `files` fields. Content-Type is left unset so
// the browser adds the multipart boundary itself.
export const uploadNoticeAttachmentsReq = (action) => {
    const url = apiEndpoints.SOCIETY_NOTICE_ATTACHMENTS_API.replace(':id', action.data.id);
    const files = action.data?.files || [];
    const formData = new FormData();
    files.forEach((f) => formData.append('files', f));
    return axiosInstance.post(url, formData)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Attachments uploaded');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to upload attachments');
            return err;
        });
};

export const deleteNoticeAttachmentReq = (action) => {
    const url = apiEndpoints.SOCIETY_DELETE_NOTICE_ATTACHMENT_API
        .replace(':id', action.data.id)
        .replace(':attachmentId', action.data.attachmentId);
    return axiosInstance.delete(url)
        .then(res => {
            if (res?.success) notify('success', res.message || 'Attachment removed');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to remove attachment');
            return err;
        });
};

// Resident confirms they have read a notice flagged requires_acknowledgement.
export const acknowledgeNoticeReq = (action) => {
    const url = apiEndpoints.SOCIETY_ACKNOWLEDGE_NOTICE_API.replace(':id', action.data.id);
    const { id, onSuccess, onError, ...payload } = action.data || {};
    return axiosInstance.post(url, clean(payload))
        .then(res => {
            if (res?.success) notify('success', res.message || 'Marked as read');
            return res;
        })
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to acknowledge notice');
            return err;
        });
};

export const getNoticeAcknowledgementsReq = (action) => {
    const url = apiEndpoints.SOCIETY_GET_NOTICE_ACKNOWLEDGEMENTS_API.replace(':id', action.data.id);
    return axiosInstance.get(url, {
        params: clean({
            society_id: action.data?.societyId,
            page: action.data?.page || 1,
            limit: action.data?.limit || 100,
        }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load acknowledgements');
            return err;
        });
};

export const getNoticeEnumsReq = () => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_NOTICE_ENUMS_API)
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load enums');
            return err;
        });
};

// Members (with owner/tenant/employee kinds and their flats), flats and wings
// for the targeting pickers. One call rather than three.
export const getNoticeAudienceOptionsReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_NOTICE_AUDIENCE_OPTIONS_API, {
        params: clean({ society_id: action.data?.societyId }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load targeting options');
            return err;
        });
};

export const getNoticeStatsReq = (action) => {
    return axiosInstance.get(apiEndpoints.SOCIETY_GET_NOTICE_STATS_API, {
        params: clean({ society_id: action.data?.societyId, notice_type: action.data?.noticeType }),
    })
        .then(res => res)
        .catch((err) => {
            notify('error', err?.response?.data?.message || 'Failed to load stats');
            return err;
        });
};
